import AccordionCustom from "@components/Accordion/AccordionCustom";
import BannerModal from "@components/BannerModal";
import BlogOrCaseStudy from "@components/BlogsOrCaseStudy";
import schema from "@components/faqSchema/whatsapp-chat-button-faqSchema.json";
import NewLayout from "@components/new-ui/Layout";
import PageMainTitleCardV2 from "@components/PageMainTitleCardV2";
import BenefitsCardWrap from "@components/WAChatbutton/BenefitsCardWrap";
import PageSummaryBox, { PageSummaryBoxProps } from "@components/WAChatbutton/PageSummaryBox";
import WhatsappWidgetGenerator from "@components/WAChatbutton/WhatsappWidgetGenerator";
import Head from "next/head";
import React, { useRef } from "react";
const scriptText = JSON.stringify(schema, null, 2);

const accordionCustomInputItems = [
    {
        question: "What is a WhatsApp Chat Widget and how does it work?",
        answer: "A WhatsApp Chat Widget is a button or chat window you can embed on your website that allows visitors to initiate a chat conversation with your business directly through WhatsApp.  When clicked, it opens a chat window within the website where visitors can send messages and ask questions. ",
    },
    {
        question: "Is a WhatsApp Chat Widget free?",
        answer: "Yes! It’s completely free. Customize your WhatsApp widget with Gallabox free tool and explore your WhatsApp business potential now!",
    },
    {
        question: "How do I install a WhatsApp Chat Widget on my website?",
        answer: "Gallabox WhatsApp Chat Widget provides easy installation processes. This typically involves generating a code snippet and adding it to your website's code.",
    },
    {
        question: "Do I need a business WhatsApp account to use a WhatsApp Chat Widget?",
        answer: "No, you just need a WhatsApp number to use a WhatsApp Chat Widget.",
    },
    {
        question: "How does the WhatsApp button on a website work?",
        answer: "Generate your free WhatsApp chat button with our simple tool. Customize it to match your website, then copy and paste the code. Boom! Visitors can chat with you instantly on WhatsApp!",
    },
];

const pageSummaryContent: PageSummaryBoxProps["summaryItems"] = [
    {
        title: "Benefits of Using a WhatsApp Chat Widget",
        description:
            "WhatsApp chat buttons can pique the interest of high-intent leads browsing your website and nudge them to explore your offerings over a conversation. Leave less room for indecision from your potential buyers by bringing them to WhatsApp and move the needle faster through your lead lifecycle.",
    },
    {
        title: "Make reluctant prospects more conversation ready",
        description: "Direct website traffic to your WhatsApp Business account, where your sales folks can have more control over  the prospects’ buying journey.",
    },
    {
        title: "Qualify leads before getting into a live conversation",
        description: "Boost your sales and marketing teams’ productivity by setting up lead qualification workflows within the WhatsApp chat widget.",
    },
    {
        title: "Shorten sales cycles with AI-assisted buying",
        description: "Accelerate your time to move between lead creation and deal closure with an intuitive mix of WhatsApp automation and human interaction, powered by Gen AI.",
    },
];
const BlogOrCaseStudyBannerContent = [
    {
        mediaUrl: "https://assets.gallabox.com/gb-home/ai-whatsapp-template-generator/og.png",
        bannerTitle: "Create AI-generated WhatsApp Message Templates for your Business scenarios",
        bannerDescription:
            "Streamline your template creation process with Gallabox’s Free AI WhatsApp Message Template Generator. Produce AI-generated templates with a text prompt to reduce template rejection and failure rates and create consistent, high-quality messages.",
        redirectUrl: "/ai-whatsapp-template-generator",
        buttonText: "Generate Now",
    },
    {
        mediaUrl: "https://assets.gallabox.com/gb-home/whatsapp-qr-generator/qr-og.png",
        bannerTitle: "Get More Leads and Sales with a WhatsApp QR Code Generator",
        bannerDescription:
            "Whether your users are offline or online, let them reach out to you by generating a QR code that takes them to your WhatsApp Business account. Improve engagement rates by setting up a seamless handover to WhatsApp.",
        redirectUrl: "/whatsapp-qr-code-generator",
        buttonText: "Generate Now",
    },
];

const WhatsAppWidget: React.FC = () => {
    const widgetFormRef = useRef<HTMLDivElement>(null);
    const handleScrollToWidgetGenerator = () => {
        if (widgetFormRef && widgetFormRef.current) {
            widgetFormRef.current.scrollIntoView({ behavior: "smooth" });
        }
    };
    return (
        <NewLayout
            title="Free WhatsApp Chat Button — Add Click-to-Chat on Your Website"
            description="Create a WhatsApp Chat Button for your website in minutes. Let customers chat instantly. Free, easy, and no coding required!"
            canonical="https://gallabox.com/whatsapp-chat-button"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/wa-chat-widget/og.png" }}
            keywords="WhatsApp Chat button, WhatsApp Chat Widget, How to create chat widget for free"
            schema={{
                heroOrOgImageAlt: "whatsapp-chat-button-image",
                heroOrOgImageDescription: "WhatsApp Chat Button: Add Instant Chat to Your Website",
                heroOrOgImageUrl: "https://assets.gallabox.com/gb-home/cta-banner.png",
                softwareFeatureName: "whatsapp-chat-button",
                faq: accordionCustomInputItems,
            }}
        >
            <Head>
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: scriptText }} />
            </Head>

            <BannerModal />

            <div className="lg:my-16 md:my-14 mb-8 mt-4 lg:mx-0 mx-3">
                <PageMainTitleCardV2
                    chipTitle="WhatsApp Chat Button"
                    mainTitle="WhatsApp Chat Button: Add Instant Chat to Your Website"
                    description="Generate a free WhatsApp Chat Widget for your website and landing pages in no time. Spark a conversation with your visitors right when their intent is high. "
                    buttonText="Generate My Template"
                    scrollHandler={handleScrollToWidgetGenerator}
                />
            </div>

            <div id="widget-generator" className="lg:pt-0 pt-5">
                <WhatsappWidgetGenerator widgetGeneratorRef={widgetFormRef} />
            </div>

            <PageSummaryBox
                summaryItems={pageSummaryContent}
                mediaURL="https://assets.gallabox.com/gb-home/wa-chat-widget/pagesummary-final.png"
                mediaAltTxt="Benefits of Using a WhatsApp Chat Widget"
                containerStyle="lg:w-3/5"
                imageStyle="lg:w-2/5"
            />
            <div className="flex justify-center mt-6">
                <a
                    href="https://bit.ly/GallaboxDemo"
                    target="_blank"
                    rel="noreferrer"
                    className="w-fit px-6 py-2.5 bg-blue-500 rounded-md text-base font-bold text-white hover:no-underline"
                >
                    Get a free 1:1 consultation
                </a>
            </div>
            <div className="flex flex-col items-center gap-20 max-w-6xl mx-auto lg:pt-20 pt-10">
                <div className="lg:pb-0 pb-4">
                    <div className="flex flex-col gap-6">
                        <h2 className="lg:text-3xl text-2xl font-semibold max-w-4xl mx-auto w-full text-center px-2 text-gray-700">
                            Why Choose Gallabox for WhatsApp Chat Widget?
                        </h2>
                        <p className="text-base text-center text-gray-600">
                            Gallabox offers much more than a simple chat widget to take your customers to your WhatsApp Business Account. Here are four reasons why Gallabox would
                            be a right fit for you:
                        </p>
                    </div>
                </div>
                <BenefitsCardWrap />
            </div>
            <div className="mx-auto mt-10 w-fit px-6 py-2.5 bg-blue-500 rounded-md">
                <a href="https://gallabox.com/whatsapp-business-api" target="_blank" rel="noreferrer">
                    <button type="button" className="text-base font-bold text-white focus:outline-none">
                        Learn More
                    </button>
                </a>
            </div>
            <div className="lg:max-w-6xl lg:mx-auto w-full">
                <BlogOrCaseStudy mainTitle="Free Tools to Grow Your Business on WhatsApp" mainDescription="" bannerContent={BlogOrCaseStudyBannerContent} />
            </div>
            <div className="lg:pt-20 pt-16">
                <section className="max-w-6xl mx-auto px-4">
                    <div className="flex flex-col justify-center lg:gap-4 gap-2 items-center pb-6">
                        <h3 className="lg:text-4xl text-3xl lg:text-center md:text-center text-left font-bold text-gray-700">
                            WhatsApp Chat Widget:
                            <br /> Frequently Asked Questions
                        </h3>
                    </div>
                    <AccordionCustom title="WhatsApp Chat Widget" items={accordionCustomInputItems} />
                </section>
            </div>
            <div
                className="flex flex-col items-center gap-7 py-10 max-w-6xl mx-auto rounded-lg mb-10"
                style={{
                    background: "linear-gradient(0deg, rgba(210, 233, 255, 0.80) 0%, rgba(210, 233, 255, 0.80) 100%)",
                }}
            >
                <p className="text-2xl font-bold text-gray-700 text-center mx-5 lg:mx-44">
                    Don’t Just Continue Your Sales Conversations on WhatsApp Turn Them Into Conversions with Gallabox!
                </p>
                <a
                    href="https://app.gallabox.com/signup"
                    target="_blank"
                    rel="noreferrer"
                    className="px-4 py-2 lg:px-8 text-white font-bold rounded-md hover:no-underline focus:outline-none hover:shadow-lg bg-blue-500 hover:bg-blue-600 transition duration-300 ease-in-out w-fit cursor-pointer"
                >
                    Start Free Trial
                </a>
            </div>
        </NewLayout>
    );
};

export default WhatsAppWidget;
